import { IconButton } from './IconButton';
import React from 'react';
import { IconEdit, IconLocation, IconPen, IconShare, IconStar, IconStarSolid } from './QuirkIcons';
import { useShare } from '../hooks/useShare';
import { IonButtons, IonToolbar, useIonToast } from '@ionic/react';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useUserHasRole } from '../hooks/useUserHasRole';
import { useHistory } from 'react-router-dom';
import { BUSINESS_CHECKIN_TOPIC, Business, EmptyBusinessCheckIn, EmptyBusinessPost } from 'model';
import { useStarBusinessMutation } from '../api/StarredBusinessApi';
import { useCreatePostMutation } from '../api/PostApi';
import { isMutationError } from '../util/error';
import { createCheckInTitle } from '../util/checkIn';
import styles from './BusinessDetailToolbar.module.css';
import classNames from 'classnames';
import { RealUserOnly } from '../pages/admin/RealUserOnly';
import { useBusinessIsStarred } from '../hooks/useBusinessIsStarred';
import { getBackendURL, useBackendDomain } from '../hooks/useBackendDomain.js';

export interface BusinessDetailToolbarProps {
	business: Business;
	size?: number;
	className?: string;
}

export const BusinessDetailToolbar: React.FC<BusinessDetailToolbarProps> = ({
	business,
	size = 18,
	className
}: BusinessDetailToolbarProps) => {
	const [domain] = useBackendDomain();
	const { handleShare } = useShare({
		message: getBackendURL('/api/sharing/' + business?._id, domain || window.location.origin),
		header: 'Share this business'
	});
	const [toggleStar, { isLoading: isUpdating }] = useStarBusinessMutation();
	const user = useCurrentUser();
	const isAdmin = useUserHasRole('admin');
	const isOwner = business.owner === user._id && business.claimStatus === 'claimed';
	const isStarred = useBusinessIsStarred(business._id, user._id);
	const isSubmitter =
		business.submittedBy === user._id &&
		business.submissionStatus !== '' &&
		business.submissionStatus !== 'approved';
	const isPending = business.submissionStatus === 'pending';
	const history = useHistory();
	const handleEditClick = () => {
		history.push(`/main/bus/edit/${business._id}`);
	};
	const handleShareClick = () => {
		handleShare();
	};
	const handleStarClick = () => {
		toggleStar({ businessId: business._id, userId: user._id });
	};
	const [showToast] = useIonToast();
	const [createPost, { isLoading: isCreatingPost }] = useCreatePostMutation();
	const handleCreatePostClick = async () => {
		const result = await createPost({
			post: {
				...(isOwner ? EmptyBusinessPost : EmptyBusinessCheckIn),
				businessId: business._id,
				title: isOwner ? '' : createCheckInTitle(user, business),
				topic: isOwner ? '' : BUSINESS_CHECKIN_TOPIC,
				author: user._id
			}
		});
		if (isMutationError(result)) {
			showToast({
				duration: 1500,
				color: 'danger',
				position: 'top',
				message: 'There was an error creating the post'
			});
		} else {
			history.push(`/main/latest/bus/${business._id}/edit/${result.data._id}`);
		}
	};

	return (
		<IonToolbar className={classNames(styles.BusinessDetailToolbar, className)}>
			<IonButtons>
				{(isOwner || isAdmin || isSubmitter) && (
					<IconButton icon={<IconEdit size={size} />} onClick={handleEditClick} label="Edit" />
				)}
				{!isPending && (
					<>
						<IconButton
							icon={isOwner ? <IconPen size={size} /> : <IconLocation />}
							onClick={handleCreatePostClick}
							disabled={isCreatingPost}
							label={isOwner ? 'Post' : 'Check-In'}
						/>
						<RealUserOnly>
							<IconButton
								iconColor="#ffc409"
								icon={isStarred ? <IconStarSolid size={size} /> : <IconStar size={size} />}
								disabled={isUpdating || isStarred === undefined}
								onClick={handleStarClick}
								label={isStarred ? 'Unfavorite' : 'Favorite'}
							/>
						</RealUserOnly>
						<IconButton icon={<IconShare size={size} />} onClick={handleShareClick} label="Share" />
					</>
				)}
			</IonButtons>
		</IonToolbar>
	);
};
